import { HotelStatus } from '../../../types/HotelFromApi';
import { BookingStatus, PaymentMethod } from '../constants';
import request from '../request';

type RequestData = {
  bookingId: string;
  pinCode?: string;
  authToken?: string;
  accountId?: string;
  idToken?: string;
  locale?: string;
};

type Room = Partial<{
  adultMaleCount: number;
  adultFemaleCount: number;
  seniorElementarySchoolChildCount: number;
  juniorElementarySchoolChildCount: number;
  infantWithMealCount: number;
  infantWithFutonCount: number;
  infantWithMealAndFutonCount: number;
  infantWithoutMealAndFutonCount: number;
}>;

type CancelPenalty = Partial<{
  type: 'cancel_policy' | 'no_show';
  deadline: number;
  percentage: number;
  amount: number;
}>;

type Discount = Partial<{
  discountAmount: number;
  discountDiv: 'tabist' | 'property' | 'partner';
  discountName: string;
}>;

export type StayDate = Partial<{
  date: string;
  planCode: string;
  planName: string;
  description: string;
  breakfast: boolean;
  lunch: boolean;
  dinner: boolean;
  planImageUrl: string;
  planPenalties: {
    cancelPenalties: {
      type: string;
      amount: number;
      deadline: number;
      percentage: number;
    }[];
    penaltyDescription: string;
  };
  forecastCancellationFee: {
    fee: number;
    cancelPolicy: {
      percentage: number;
      amount: number;
    };
  };
  rooms: {
    roomIndex: number;
    guests: {
      guestIndex: number;
      category: string;
    }[];
  }[];
}>;

type Plan = Partial<{
  /**
   * e.g. 14763973
   */
  planCode: string;
  /**
   * e.g. 2022-04-27
   */
  stayDate: string;
  /**
   * e.g.【朝食】ツインプラン
   */
  planName: string;
  description: string;
  /**
   * e.g. "https://test472.tl-lincoln.net/accomodation/accomodation_image/Z/Z02347/00013364.jpg"
   */
  planImageUrl: string;
  breakfast: boolean;
  lunch: boolean;
  dinner: boolean;
  planPenalties: Partial<{
    penaltyDescription: string;
    cancelPenalties: CancelPenalty[];
  }>;
  forecastCancellationFee: Partial<{
    fee: number;
    cancelPolicy: null;
  }>;
}>;

export type ResponseData = Partial<{
  /**
   * e.g. TB1234ABCD
   */
  bookingId: string;
  /**
   * e.g. 778546
   */
  pinCode: string;

  /**
   * Status of the booking
   * * `reserved`: the booking has been created
   * * `cancelled`: the booking has been cancelled
   */
  bookingStatus: BookingStatus;

  /**
   * e.g. 2022-04-27
   */
  checkInDate: string;

  /**
   * e.g. 18:00
   */
  checkInTime: string;

  /**
   * e.g. 2022-04-28
   */
  checkOutDate: string;

  /**
   * e.g. 10:00
   */
  checkOutTime: string;
  /**
   * e.g. 5000
   */
  totalAmount: number;
  /**
   * e.g. 5000
   */
  totalCancellationFee: number;
  /**
   * e.g. 0
   */
  totalDiscountedAmount: number;
  /**
   * e.g. 0
   */
  totalPreviousAmount: number;
  /**
   * e.g. 0
   */
  totalRefundFee: number;
  rooms: Room[];
  roomDetails: Partial<{
    /**
     * e.g. 【禁煙】ツイン
     */
    roomName: string;
    /**
     * e.g. 5
     */
    roomTypeCode: string;
    amenities: {
      highSpeedInternet: boolean;
      privateBathroom: boolean;
      toilet: boolean;
    };
    bedTypeCode: 'twin' | 'triple';

    /**
     * e.g. ["https://test472.tl-lincoln.net/accomodation/accomodation_image/R/R03894/00013358.jpg"]
     */
    roomImageUrl: string[];
  }>;
  payment: Partial<{
    paymentMethod: PaymentMethod;
    status: 'captured';
  }>;
  customer: Partial<{
    firstName: string;
    lastName: string;
    firstNameKana: string;
    lastNameKana: string;
    email: string;
    phoneNumber: string;
    postalCode: string;
    address: string;
    countryCode: string;
    fullNameKana: string;
  }>;
  hotel: Partial<{
    name: string;
    address: string;
    propertyId: string;
    tel: string;
    status: HotelStatus;
    customInformation: {
      plain: string;
      rich: {
        title: string;
        content: string;
      }[];
    };
  }>;
  isUpdatedByAdmin: boolean;
  isUpdatedByJira: boolean;
  guestArrivalTime: string;
  plans: Plan[];
  authToken: string;
  discounts: Discount[];
  stayDates: StayDate[];
  isArchived: boolean;
}>;

const getBooking = async (body: RequestData, signal?: AbortSignal) => {
  const parameters = {
    locale: body?.locale,
  } as Record<string, string>;

  if (body?.pinCode) {
    parameters.pinCode = body?.pinCode;
  }

  if (body?.accountId) {
    parameters.accountId = body?.accountId;
  }

  const result = await request<ResponseData, RequestData>({
    url: `${process.env.NEXT_PUBLIC_BFF_URL}/bookings/${body?.bookingId}?${new URLSearchParams(
      parameters
    ).toString()}`,
    method: 'GET',
    headers: {
      Authorization:
        body?.idToken || body?.authToken ? `Bearer ${body?.idToken || body?.authToken}` : '',
    },
    signal,
  });

  return result;
};

export default getBooking;
